import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import axios from "axios";
import toast from "react-hot-toast";
import AxiosInstance from "../utils/axiosInstance";
import Pagination from "../components/Layout/Pagination";
import loader from "../assets/loader.svg";
import paginationValue from "../common/paginationValue.json";

export default function AgencyReport() {
  const [currentPage, setCurrentPage] = useState(paginationValue.currentPage);
  const [pageLimit, setPageLimit] = useState(paginationValue.limit);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]); // New state for filtered data

  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (currentPage > 0) {
      getDirectoryList();
    }
  }, [currentPage]);

  useEffect(() => {
    // Filter data based on search value
    if (searchValue === "") {
      setFilteredDataList(dataList); // If search is empty, show all data
    } else {
      const filteredData = dataList.filter((item) =>
        item.agency_name.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredDataList(filteredData); // Update the filtered list
    }
  }, [searchValue, dataList]);

  const getDirectoryList = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setShowLoader(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const queryParams = new URLSearchParams({
      page: currentPage,
      limit: pageLimit,
      search_text: searchValue,
    });

    AxiosInstance.get(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_GET_AGENCY_ASSOCIATE_CERTIFICATES}`,
      { signal: controller.signal, params: queryParams }
    )
      .then((response) => {
        if (response.data?.data) {
          setDataList(response.data?.data);
          setPageLimit(response.data?.limit);
          setTotalRecords(response.data?.total_records);
          setTotalPages(response.data?.total_pages);
          setCurrentPage(response.data?.page);
          setShowLoader(false);
          toast.success(response.data?.message);
        } else {
          setDataList([]);
          setShowLoader(false);
          toast.success(response.data?.message);
        }
      })
      .catch((error) => {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          // Ignore cancelled requests
        } else {
          toast.error(error?.message);
          setShowLoader(false);
        }
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value); // Update the search value
  };

  return (
    <>
      <Header />
      <div className="bg-primary pt-5 pb-20">
        <div className="container">
          <div className="row">
            <div className="col-12 col-md bg-primary mb-3">
              <p className="text-white point-22"></p>
              <h1 className="text-secondary fw-boldest point-42">
                Agency Report
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white py-3">
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="card w-100 mt-n20 bg-white rounded-3 shadow-sm mb-5">
                <div className="card-body p-3">
                  <div className="row">
                    <div className="col-12 col-md-auto">
                      <div className="input-group mb-3">
                        <span
                          className="input-group-text text-uppercase text-secondary fw-bold border-0 bg-white rounded-0"
                          id="basic-addon1"
                        >
                          Filter:
                        </span>
                      </div>
                    </div>

                    <div className="col-12 col-md-3">
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control fw-bolder text-white bg-secondary rounded-0 border border-1 border-secondary placeholder-white point-18"
                          placeholder="Search"
                          aria-label="Search"
                          aria-describedby="button-addon2"
                          value={searchValue}
                          onChange={handleSearchChange} // Handle search input change
                        />
                      </div>
                    </div>

                    <div className="col-12 col-md mb-2 float-end">
                      <span className="point-22 text-black-50 fw-light">
                        <b>Total: {totalRecords}</b>
                      </span>
                    </div>
                  </div>

                  <div>
                    {!showLoader ? (
                      <div
                        className="table-responsive"
                        style={{
                          maxHeight: "400px",
                          overflowY: "auto",
                          position: "relative", // Ensures that the sticky header works
                        }}
                      >
                        <table className="table table-bordered table-hover centered">
                          <thead
                            className="table-dark sticky-top"
                            style={{
                              position: "sticky",
                              top: "0",
                              zIndex: "1", // Ensures the header stays above the body content
                            }}
                          >
                            <tr>
                              <th>AGENCY NAME</th>
                              <th>TOTAL CERTIFICATES</th>
                              <th>MAPPED WITH WORKTYPES</th>
                              <th>UNMAPPED WITH WORKTYPES</th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredDataList.length > 0 ? (
                              filteredDataList.map((row, index) => (
                                <tr key={index}>
                                  <td>{row.agency_name}</td>
                                  <td>{row.total_certificates}</td>
                                  <td>{row.certificates_with_reports}</td>
                                  <td>
                                    {row.total_certificates -
                                      row.certificates_with_reports}{" "}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4">No data available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="loader">
                        <img src={loader} alt="loading" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
